import { FormEvent, useEffect, useState } from 'react';
import { PlanningCycle, PlanningCycleUpsertPayload } from '../../services/strategicPlanningApi';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { PlanningCancellationReasonField } from './PlanningCancellationReasonField';

type PlanningCycleFormModalProps = {
  open: boolean;
  initial?: PlanningCycle | null;
  existingCycles?: PlanningCycle[];
  onClose: () => void;
  onSubmit: (payload: PlanningCycleUpsertPayload) => Promise<void>;
};

const CYCLE_KINDS = [
  { value: 'Annual', label: 'Anual' },
  { value: 'Quarterly', label: 'Trimestral' },
  { value: 'Sprint', label: 'Sprint' },
  { value: 'Project', label: 'Projeto' },
  { value: 'Commercial', label: 'Comercial' },
  { value: 'MpsBrLevelC', label: 'MPS.Br Nível C' },
  { value: 'Custom', label: 'Personalizado' },
];

const CYCLE_STATUSES = [
  { value: 'Draft', label: 'Rascunho' },
  { value: 'Active', label: 'Ativo' },
  { value: 'Closed', label: 'Encerrado' },
  { value: 'Cancelled', label: 'Cancelado' },
];

function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function endOfYearIsoDate(): string {
  const year = new Date().getFullYear();
  return `${year}-12-31`;
}

export function PlanningCycleFormModal({
  open,
  initial,
  existingCycles = [],
  onClose,
  onSubmit,
}: PlanningCycleFormModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('Annual');
  const [status, setStatus] = useState('Draft');
  const [cancellationReason, setCancellationReason] = useState('');
  const [startDate, setStartDate] = useState(todayIsoDate());
  const [endDate, setEndDate] = useState(endOfYearIsoDate());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setName(initial?.name ?? '');
    setDescription(initial?.description ?? '');
    setKind(initial?.kind ?? 'Annual');
    setStatus(initial?.status ?? 'Draft');
    setCancellationReason(initial?.cancellationReason ?? '');

    if (initial) {
      setStartDate(initial.startDate);
      setEndDate(initial.endDate);
      return;
    }

    const sortedCycles = [...existingCycles].sort((left, right) => left.endDate.localeCompare(right.endDate));
    const lastCycle = sortedCycles[sortedCycles.length - 1];
    if (lastCycle) {
      const nextStart = addDays(lastCycle.endDate, 1);
      setStartDate(nextStart);
      setEndDate(addDays(nextStart, 364));
    } else {
      setStartDate(todayIsoDate());
      setEndDate(endOfYearIsoDate());
    }
  }, [open, initial, existingCycles]);

  if (!open) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (status === 'Cancelled' && !cancellationReason.trim()) {
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        kind,
        status,
        cancellationReason: status === 'Cancelled' ? cancellationReason.trim() : undefined,
        startDate,
        endDate,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell open={open}>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
            <h2 className="erp-card-title">{initial ? 'Editar ciclo' : 'Novo ciclo'}</h2>
            <p className="erp-card-subtitle">Período de planejamento estratégico da organização.</p>
          </div>

          <div className="erp-card-body min-h-0 flex-1 space-y-4 overflow-y-auto">
          <label className="block">
            <span className="erp-label">Nome</span>
            <input
              className="erp-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              maxLength={200}
            />
          </label>

          <label className="block">
            <span className="erp-label">Descrição</span>
            <textarea
              className="erp-input min-h-[80px]"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={4000}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Tipo</span>
              <select className="erp-input" value={kind} onChange={(event) => setKind(event.target.value)}>
                {CYCLE_KINDS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="erp-label">Status</span>
              <select
                className="erp-input"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {CYCLE_STATUSES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {status === 'Cancelled' ? (
            <PlanningCancellationReasonField
              value={cancellationReason}
              onChange={setCancellationReason}
            />
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Início</span>
              <input
                type="date"
                className="erp-input"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                required
              />
            </label>

            <label className="block">
              <span className="erp-label">Fim</span>
              <input
                type="date"
                className="erp-input"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                required
              />
            </label>
          </div>

          </div>

          <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
            <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="erp-btn-primary" disabled={saving}>
              {saving ? 'Salvando…' : initial ? 'Salvar' : 'Criar ciclo'}
            </button>
          </div>
        </form>
    </ModalPortalShell>
  );
}
